<template>
  <div>
    <Menu/>

    <header class="hero hero-buy">
      <div class="container">
        <div class="hero-topbar d-flex align-items-center justify-content-between">

          <div class="brand-lockup">
            <a href="/home" class="d-flex align-items-center text-decoration-none">
              <img src="https://www.stratitude.co.za/chas-international/dubai/images/chas-logo.svg" alt="Chas Everitt" class="brand-logo" loading="lazy" />
            </a>

            <span class="brand-divider d-none d-md-inline-block" aria-hidden="true"></span>

            <div class="d-none d-md-block">
              <a href="https://www.leadingre.com/" class="d-flex align-items-center text-decoration-none" target="_blank" rel="noopener noreferrer">
                <img src="https://www.stratitude.co.za/chas-international/dubai/images/leading-logo1.png" alt="Leading Real Estate" class="partner-logo" loading="lazy" />
              </a>
            </div>
          </div>

          <nav class="d-none d-lg-flex hero-nav align-items-center">
            <a class="nav-link" href="/properties">Buy</a>
            <a class="nav-link" href="/sell">Sell</a>
            <a class="nav-link" href="/developments">Developments</a>
            <a class="nav-link" href="/commercial">Commercial</a>
          </nav>

          <button
            class="btn hamburger d-lg-none"
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#mobileNav"
          >
            <i class="bi bi-list" style="font-size:1.25rem;"></i>
          </button>
        </div>

        <div class="pt-5 pb-4">
          <h1 class="hero-title mb-3">Properties<br/>in <strong>Dubai</strong></h1>
        </div>
      </div>
    </header>

    <div v-if="loading" class="py-5 text-center">
      Loading listings...
    </div>

    <section class="search-strip" v-if="!loading">
      <div class="container">
        <form class="row g-2 align-items-center search-strip-desktop" @submit.prevent="applyFiltersToUrl">
          <div class="col-12 col-md-6 col-xl-2">
            <select class="form-select" v-model="selectedListingType">
              <option value="">LISTING TYPE</option>
              <option
                v-for="type in listingTypes"
                :key="type"
                :value="type"
              >
                {{ type }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md-6 col-xl-2">
            <select class="form-select" v-model="selectedPropertyType">
              <option value="">PROPERTY TYPE</option>
              <option
                v-for="type in propertyTypes"
                :key="type"
                :value="type"
              >
                {{ type }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md-6 col-xl-3 position-relative">
            <input
              type="text"
              class="form-control w-100"
              placeholder="ENTER A LOCATION"
              v-model="searchText"
              @focus="openLocationSuggestions"
              @input="openLocationSuggestions"
              @keydown.esc="closeLocationSuggestions"
              @blur="closeLocationSuggestions"
            />

            <ul
              v-if="showLocationSuggestions && locationSuggestions.length > 0 && searchText != ''"
              class="list-group search-suggest"
            >
              <li
                v-for="suggestion in locationSuggestions"
                :key="suggestion"
                class="list-group-item"
                @mousedown.prevent="selectLocationSuggestion(suggestion)"
              >
                <div style="cursor: pointer;">
                  {{ suggestion }}
                </div>
              </li>
            </ul>
          </div>

          <div class="col-6 col-md-3 col-xl-1">
            <select class="form-select" v-model.number="selectedBeds">
              <option :value="0">BEDS</option>
              <option
                v-for="bed in bedOptions"
                :key="bed"
                :value="bed"
              >
                {{ bed }}+
              </option>
            </select>
          </div>

          <div class="col-6 col-md-3 col-xl-1">
            <select class="form-select" v-model.number="selectedBaths">
              <option :value="0">BATHS</option>
              <option
                v-for="bath in bathOptions"
                :key="bath"
                :value="bath"
              >
                {{ bath }}+
              </option>
            </select>
          </div>

          <div class="col-12 col-md-6 col-xl-2">
            <select class="form-select" v-model.number="selectedMaxPrice">
              <option :value="0">MAX PRICE</option>
              <option
                v-for="price in priceOptions"
                :key="price"
                :value="price"
              >
                {{ formatCurrency(price) }}
              </option>
            </select>
          </div>

          <div class="col-12 col-xl-1">
            <button class="btn btn-primary w-100 search-submit-btn" type="submit">
              Search
            </button>
          </div>
        </form>
      </div>
    </section>

    <section v-if="!loading" class="py-5">
      <div class="container text-center">
        <div class="row mb-4">
          <div class="col-12">
            <div class="sort-strip d-inline-flex gap-2 align-items-center justify-content-center flex-wrap">
              <button
                type="button"
                class="btn btn-primary filter-btn sort-btn"
                :class="{ active: highToLow }"
                @click="setHighToLow"
              >
                Price high to low
              </button>

              <button
                type="button"
                class="btn btn-primary filter-btn sort-btn"
                :class="{ active: !highToLow }"
                @click="setLowToHigh"
              >
                Price low to high
              </button>

              <button
                type="button"
                class="btn btn-outline-secondary filter-btn sort-btn"
                @click="clearFilters"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12 text-start">
            <p class="mb-0">
              Showing {{ paginatedListings.length }} of {{ filteredListings.length }} Dubai properties
            </p>
          </div>
        </div>

        <div class="row g-4" v-if="paginatedListings.length > 0">
          <div
            class="col-12 col-md-6 col-lg-4"
            v-for="listing in paginatedListings"
            :key="listing.source_type + '-' + listing.id"
          >
            <div class="listing-card h-100">
              <a class="listing-card__media" :href="propertyUrl(listing)">
                <img
                  :src="featuredImage(listing)"
                  class="listing-card__img"
                  :alt="cleanText(listing.marketing_heading)"
                  loading="lazy"
                />
              </a>

              <div class="listing-card__body text-start">
                <div class="listing-card__tags">
                  <span class="tag" v-if="listing.listing_type">{{ listing.listing_type }}</span>
                  <span class="tag" v-if="listing.property_type">{{ listing.property_type }}</span>
                </div>

                <div class="listing-card__price">
                  <strong>{{ displayPrice(listing) }}</strong>
                  <span v-if="listing.price_term"> / {{ listing.price_term }}</span>

                  <template v-if="hasConvertedPrice(listing)">
                    <div class="converted-price">
                      Approx. {{ displayConvertedPrice(listing, 'ZAR') }}
                    </div>
                    <div class="converted-price">
                      Approx. {{ displayConvertedPrice(listing, 'USD') }}
                    </div>
                  </template>
                </div>

                <p class="listing-card__title text-uppercase">
                  <strong>{{ cleanText(listing.marketing_heading) }}</strong>
                </p>

                <p class="listing-card__meta">
                  {{ listing.loc_suburb }}, {{ listing.loc_region }}
                </p>

                <div class="listing-card__features">
                  <span v-if="showNumber(listing.bedrooms)">{{ listing.bedrooms }} Beds</span>
                  <span v-if="showNumber(listing.bathrooms)">{{ listing.bathrooms }} Baths</span>
                  <span v-if="showNumber(listing.building_size)">
                    {{ formatSize(listing.building_size, listing.building_measurement_type) }}
                  </span>
                </div>

                <div class="listing-card__cta mt-3">
                  <a class="btn btn-cta filter-btn" :href="propertyUrl(listing)">
                    VIEW <i class="bi bi-arrow-right"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="py-5">
          <p>No Dubai properties match your current filters.</p>
          <button type="button" class="btn btn-primary" @click="clearFilters">
            View all properties
          </button>
        </div>

        <div class="row mt-4" v-if="totalPages > 1">
          <div class="col-12 d-flex justify-content-center gap-2 align-items-center">
            <button
              class="btn btn-primary filter-btn"
              type="button"
              @click="prevPage"
              :disabled="currentPage == 1"
            >
              Previous
            </button>

            <span>Page {{ currentPage }} of {{ totalPages }}</span>

            <button
              class="btn btn-primary filter-btn"
              type="button"
              @click="nextPage"
              :disabled="currentPage == totalPages"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>

    <div
      class="offcanvas offcanvas-end offcanvas-custom"
      tabindex="-1"
      id="mobileNav"
      aria-labelledby="mobileNavLabel"
    >
      <div class="offcanvas-header">
        <h5 class="offcanvas-title text-white" id="mobileNavLabel">Menu</h5>
        <button
          type="button"
          class="btn-close btn-close-white"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>

      <div class="offcanvas-body">
        <div class="list-group list-group-flush">
          <a class="list-group-item list-group-item-action" href="/properties">Buy</a>
          <a class="list-group-item list-group-item-action" href="/sell">Sell</a>
          <a class="list-group-item list-group-item-action" href="/developments">Developments</a>
          <a class="list-group-item list-group-item-action" href="/commercial">Commercial</a>
        </div>

        <hr/>
      </div>
    </div>

    <Footer/>
  </div>
</template>

<script>
import axios from 'axios'
import Menu from '@/components/Menu.vue'
import Footer from '@/components/Footer.vue'

export default {
  components: {
    Menu,
    Footer
  },

  data() {
    return {
      residentialListings: [],
      commercialListings: [],
      loading: true,
      currencyRates: null,
      selectedListingType: '',
      selectedPropertyType: '',
      searchText: '',
      selectedBeds: 0,
      selectedBaths: 0,
      selectedMaxPrice: 0,
      currentPage: 1,
      itemsPerPage: 9,
      highToLow: true,
      showLocationSuggestions: false
    }
  },

  mounted() {
    this.setFiltersFromRouteOrStore()
    this.fetchCurrencyRates()
    this.fetchAllListings()
  },

  watch: {
    '$route.query': {
      handler() {
        this.setFiltersFromRouteOrStore()
      },
      deep: true
    },

    selectedListingType() {
      this.currentPage = 1
    },

    selectedPropertyType() {
      this.currentPage = 1
    },

    searchText() {
      this.currentPage = 1
    },

    selectedBeds() {
      this.currentPage = 1
    },

    selectedBaths() {
      this.currentPage = 1
    },

    selectedMaxPrice() {
      this.currentPage = 1
    }
  },

  computed: {
    allListings() {
      const residential = this.normaliseListings(this.residentialListings, 'residential')
      const commercial = this.normaliseListings(this.commercialListings, 'commercial')

      return residential.concat(commercial)
    },

    activeListings() {
      return this.allListings.filter((listing) => {
        const status = this.safeString(listing.status).toLowerCase()

        if (status == 'archived') {
          return false
        }

        if (!Array.isArray(listing.listing_images)) {
          return false
        }

        if (listing.listing_images.length == 0) {
          return false
        }

        return true
      })
    },

    listingTypes() {
      return this.uniqueSortedValues('listing_type')
    },

    propertyTypes() {
      return this.uniqueSortedValues('property_type')
    },

    bedOptions() {
      return this.uniqueSortedNumbers('bedrooms')
    },

    bathOptions() {
      return this.uniqueSortedNumbers('bathrooms')
    },

    priceOptions() {
      const prices = this.activeListings
        .map((listing) => {
          return this.priceNumber(listing.price)
        })
        .filter((price) => {
          return price > 0 && price != 999999999
        })

      const roundedPrices = prices.map((price) => {
        if (price <= 250000) {
          return Math.ceil(price / 50000) * 50000
        }

        if (price <= 1000000) {
          return Math.ceil(price / 100000) * 100000
        }

        return Math.ceil(price / 1000000) * 1000000
      })

      return Array.from(new Set(roundedPrices)).sort((a, b) => {
        return a - b
      })
    },

    locationSuggestions() {
      const text = this.searchText.toLowerCase()

      const locations = this.activeListings.map((listing) => {
        const suburb = listing.loc_suburb || ''
        const region = listing.loc_region || ''

        if (suburb != '' && region != '') {
          return suburb + ', ' + region
        }

        if (suburb != '') {
          return suburb
        }

        return region
      })

      return Array.from(new Set(locations))
        .filter((location) => {
          if (location == '') {
            return false
          }

          return location.toLowerCase().includes(text)
        })
        .sort()
        .slice(0, 8)
    },

    filteredListings() {
      const filtered = this.activeListings.filter((listing) => {
        if (this.selectedListingType != '') {
          if (listing.listing_type != this.selectedListingType) {
            return false
          }
        }

        if (this.selectedPropertyType != '') {
          if (listing.property_type != this.selectedPropertyType) {
            return false
          }
        }

        if (this.searchText != '') {
          const haystack = [
            listing.loc_suburb,
            listing.loc_region,
            listing.marketing_heading,
            listing.property_type,
            listing.listing_type
          ].join(' ').toLowerCase()

          if (!haystack.includes(this.searchText.toLowerCase())) {
            return false
          }
        }

        if (this.selectedBeds > 0) {
          if (this.numberValue(listing.bedrooms) < this.selectedBeds) {
            return false
          }
        }

        if (this.selectedBaths > 0) {
          if (this.numberValue(listing.bathrooms) < this.selectedBaths) {
            return false
          }
        }

        if (this.selectedMaxPrice > 0) {
          if (!this.isPOA(listing)) {
            if (this.priceNumber(listing.price) > this.selectedMaxPrice) {
              return false
            }
          }
        }

        return true
      })

      filtered.sort((a, b) => {
        const priceA = this.priceNumber(a.price)
        const priceB = this.priceNumber(b.price)

        if (this.highToLow) {
          return priceB - priceA
        }

        return priceA - priceB
      })

      return filtered
    },

    totalPages() {
      const pages = Math.ceil(this.filteredListings.length / this.itemsPerPage)

      if (pages < 1) {
        return 1
      }

      return pages
    },

    paginatedListings() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage
      const endIndex = startIndex + this.itemsPerPage

      return this.filteredListings.slice(startIndex, endIndex)
    }
  },

  methods: {
    async fetchCurrencyRates() {
      try {
        const response = await axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-currency-rates.php'
        )

        if (!response.data || response.data.success !== true) {
          return
        }

        this.currencyRates = {
          aedToZar: response.data.aedToZar,
          aedToUsd: response.data.aedToUsd,
          updatedAt: response.data.updatedAt,
          cachedAt: response.data.cachedAt
        }
      } catch (error) {
        console.log(error)
        this.currencyRates = null
      }
    },

    setFiltersFromRouteOrStore() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      const hasQuery = Object.keys(query).length > 0

      if (!hasQuery) {
        this.selectedListingType = ''
        this.selectedPropertyType = ''
        this.searchText = ''
        this.selectedBeds = 0
        this.selectedBaths = 0
        this.selectedMaxPrice = 0
        this.currentPage = 1
        return
      }

      this.selectedListingType = query.listing_type || ''
      this.selectedPropertyType = query.property_type || ''
      this.searchText = query.location || ''
      this.selectedBeds = this.numberValue(query.beds || 0)
      this.selectedBaths = this.numberValue(query.baths || 0)
      this.selectedMaxPrice = this.numberValue(query.max_price || 0)
      this.currentPage = 1
    },

    storeValue(key) {
      if (!this.$store || !this.$store.state) {
        return ''
      }

      if (this.$store.state[key] === undefined || this.$store.state[key] === null) {
        return ''
      }

      return this.$store.state[key]
    },

    storeSearchText() {
      if (!this.$store || !this.$store.state) {
        return ''
      }

      if (!this.$store.state.search) {
        return ''
      }

      return this.$store.state.search.text || ''
    },

    async fetchAllListings() {
      this.loading = true

      try {
        const residentialRequest = axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-listings-residential.php',
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        const commercialRequest = axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-listings-commercial.php',
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        const responses = await Promise.all([
          residentialRequest,
          commercialRequest
        ])

        this.residentialListings = responses[0].data
        this.commercialListings = responses[1].data
      } catch (error) {
        console.log(error)
        this.residentialListings = []
        this.commercialListings = []
      }

      this.loading = false
    },

    normaliseListings(listings, sourceType) {
      if (!Array.isArray(listings)) {
        return []
      }

      return listings
        .filter((listing) => {
          if (!listing) {
            return false
          }

          if (!listing[0]) {
            return false
          }

          return true
        })
        .map((listing) => {
          const item = Object.assign({}, listing[0])
          item.source_type = sourceType

          return item
        })
    },

    uniqueSortedValues(fieldName) {
      const values = this.activeListings
        .map((listing) => {
          return listing[fieldName]
        })
        .filter((value) => {
          return value
        })

      return Array.from(new Set(values)).sort()
    },

    uniqueSortedNumbers(fieldName) {
      const values = this.activeListings
        .map((listing) => {
          return this.numberValue(listing[fieldName])
        })
        .filter((value) => {
          return value > 0
        })

      return Array.from(new Set(values)).sort((a, b) => {
        return a - b
      })
    },

    openLocationSuggestions() {
      this.showLocationSuggestions = true
    },

    closeLocationSuggestions() {
      this.showLocationSuggestions = false
    },

    selectLocationSuggestion(suggestion) {
      this.searchText = suggestion
      this.closeLocationSuggestions()
    },

    applyFiltersToUrl() {
      this.closeLocationSuggestions()
      this.saveFiltersToStore()

      const query = {}

      if (this.selectedListingType != '') {
        query.listing_type = this.selectedListingType
      }

      if (this.selectedPropertyType != '') {
        query.property_type = this.selectedPropertyType
      }

      if (this.searchText != '') {
        query.location = this.searchText
      }

      if (this.selectedBeds > 0) {
        query.beds = this.selectedBeds
      }

      if (this.selectedBaths > 0) {
        query.baths = this.selectedBaths
      }

      if (this.selectedMaxPrice > 0) {
        query.max_price = this.selectedMaxPrice
      }

      if (this.$router) {
        this.$router.push({
          path: '/properties',
          query: query
        })
      }
    },

    saveFiltersToStore() {
      if (!this.$store || !this.$store.state) {
        return
      }

      this.$store.state.selectedListingType = this.selectedListingType
      this.$store.state.selectedPropertyType = this.selectedPropertyType
      this.$store.state.searchText = this.searchText
      this.$store.state.selectedBeds = this.selectedBeds
      this.$store.state.selectedBaths = this.selectedBaths
      this.$store.state.selectedMaxPrice = this.selectedMaxPrice
      this.$store.state.filterType = this.selectedListingType
      this.$store.state.filterProperty = this.selectedPropertyType

      if (this.$store.state.search) {
        this.$store.state.search.text = this.searchText
      }
    },

    clearFilters() {
      this.closeLocationSuggestions()
      this.selectedListingType = ''
      this.selectedPropertyType = ''
      this.searchText = ''
      this.selectedBeds = 0
      this.selectedBaths = 0
      this.selectedMaxPrice = 0
      this.currentPage = 1
      this.saveFiltersToStore()
      this.applyFiltersToUrl()
    },

    setHighToLow() {
      this.highToLow = true
      this.currentPage = 1
    },

    setLowToHigh() {
      this.highToLow = false
      this.currentPage = 1
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1
      }
    },

    propertyUrl(listing) {
      return '/property/' + listing.id
    },

    featuredImage(listing) {
      if (!listing || !Array.isArray(listing.listing_images)) {
        return ''
      }

      if (listing.listing_images.length == 0) {
        return ''
      }

      return listing.listing_images[0]
    },

    displayPrice(listing) {
      if (!listing) {
        return ''
      }

      if (this.isPOA(listing)) {
        return 'POA'
      }

      return this.formatCurrency(listing.price)
    },

    displayConvertedPrice(listing, currency) {
      if (!listing) {
        return ''
      }

      if (!this.currencyRates) {
        return ''
      }

      if (this.isPOA(listing)) {
        return ''
      }

      const price = parseInt(listing.price, 10)

      if (isNaN(price)) {
        return ''
      }

      if (price == 999999999) {
        return ''
      }

      if (currency == 'ZAR') {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: 'ZAR',
          maximumFractionDigits: 0
        }).format(price * this.currencyRates.aedToZar)
      }

      if (currency == 'USD') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0
        }).format(price * this.currencyRates.aedToUsd)
      }

      return ''
    },

    hasConvertedPrice(listing) {
      if (!listing) {
        return false
      }

      if (!this.currencyRates) {
        return false
      }

      if (this.isPOA(listing)) {
        return false
      }

      const price = parseInt(listing.price, 10)

      if (isNaN(price)) {
        return false
      }

      if (price == 999999999) {
        return false
      }

      return true
    },

    formatCurrency(value) {
      const amount = parseInt(value, 10)

      if (isNaN(amount)) {
        return 'AED 0'
      }

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        maximumFractionDigits: 0
      }).format(amount)
    },

    formatSize(value, unit) {
      const number = this.numberValue(value)

      if (number == 0) {
        return '—'
      }

      const cleanNumber = new Intl.NumberFormat('en-AE', {
        maximumFractionDigits: 0
      }).format(number)

      if (unit == 'Square Feet') {
        return cleanNumber + ' sqft'
      }

      if (unit == 'Square Metres' || unit == 'Square Meters') {
        return cleanNumber + ' sqm'
      }

      if (unit) {
        return cleanNumber + ' ' + unit
      }

      return cleanNumber
    },

    showNumber(value) {
      return this.numberValue(value) > 0
    },

    priceNumber(value) {
      const price = parseInt(value, 10)

      if (isNaN(price)) {
        return 0
      }

      return price
    },

    numberValue(value) {
      const number = parseFloat(value)

      if (isNaN(number)) {
        return 0
      }

      return number
    },

    safeString(value) {
      if (!value) {
        return ''
      }

      return value.toString()
    },

    isPOA(listing) {
      if (!listing) {
        return false
      }

      if (listing.poa === true) {
        return true
      }

      if (listing.poa == 'true') {
        return true
      }

      if (parseInt(listing.price, 10) == 999999999) {
        return true
      }

      return false
    },

    cleanText(value) {
      if (!value) {
        return ''
      }

      return value
        .toString()
        .replace(/ΓÇö/g, '—')
        .replace(/ΓÇô/g, '–')
        .replace(/ΓÇÖ/g, '’')
        .replace(/ΓÇ£/g, '“')
        .replace(/ΓÇ¥/g, '”')
        .replace(/ΓÇó/g, '•')
        .replace(/Γ£ö∩╕Å/g, '✓')
        .replace(/≡ƒôì/g, '📍')
        .replace(/ΓÇª/g, '…')
        .replace(/┬á/g, ' ')
        .replace(/&amp;/g, '&')
    }
  }
}
</script>

<style scoped>
.search-suggest {
  position: absolute;
  z-index: 20;
  width: calc(100% - 24px);
  left: 12px;
  right: 12px;
}

.tag {
  background-color: #255699;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  padding: 5px 20px;
  border-radius: 0;
  display: inline-block;
  margin-right: 5px;
  margin-bottom: 5px;
}

.listing-card__media {
  display: block;
  overflow: hidden;
}

.listing-card__img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  display: block;
}

.listing-card__features {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 14px;
}

.listing-card__price {
  margin-top: 15px;
}
.converted-price {
  font-size: 0.85em;
  font-weight: 400;
  opacity: 0.75;
  line-height: 1.3;
}

</style>